import React, { useEffect, useId, useRef, useState } from 'react';
import { UserDropdownDto } from '../../types';
import { usersApi } from '../../api/users';
import { getActiveMention, taggedUsersInText } from './messageTags';

// ─── MentionInput ─────────────────────────────────────────────────────────────

interface MentionInputProps {
  value: string;
  onChange: (text: string) => void;
  /** Users tagged so far (kept in sync with the text: removed when their "@Name" is deleted). */
  taggedUsers: UserDropdownDto[];
  onTaggedUsersChange: (users: UserDropdownDto[]) => void;
  /** Only members of this BRG can be tagged. */
  brgId?: number;
  /** Called on Enter when the tag list is not open. */
  onSubmit?: () => void;
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
}

/**
 * Single-line message input with @tagging. Typing "@" opens a type-ahead list of the BRG's
 * members that narrows as the user types; choosing one inserts "@Full Name" into the text.
 */
export function MentionInput({
  value,
  onChange,
  taggedUsers,
  onTaggedUsersChange,
  brgId,
  onSubmit,
  disabled,
  placeholder,
  style,
}: MentionInputProps) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestRef = useRef(0);
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [results, setResults] = useState<UserDropdownDto[]>([]);
  const [highlighted, setHighlighted] = useState(0);

  const isOpen = !!mention && results.length > 0;

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const closeList = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    requestRef.current++;
    setMention(null);
    setResults([]);
  };

  const searchUsers = (query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const requestId = ++requestRef.current;
      try {
        const users = await usersApi.search(query, true, brgId);
        if (requestId !== requestRef.current) return; // a newer search is in flight
        setResults(users.slice(0, 8));
        setHighlighted(0);
      } catch {
        if (requestId === requestRef.current) setResults([]);
      }
    }, 250);
  };

  const updateMention = (text: string, caret: number) => {
    const active = getActiveMention(text, caret);
    setMention(active);
    if (active) searchUsers(active.query);
    else closeList();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    onChange(text);
    // Drop tags whose "@Name" was deleted from the text
    const stillTagged = taggedUsersInText(text, taggedUsers);
    if (stillTagged.length !== taggedUsers.length) onTaggedUsersChange(stillTagged);
    updateMention(text, e.target.selectionStart ?? text.length);
  };

  const selectUser = (user: UserDropdownDto) => {
    if (!mention) return;
    const caret = mention.start + 1 + mention.query.length;
    const insert = `@${user.displayName} `;
    const next = value.slice(0, mention.start) + insert + value.slice(caret);
    onChange(next);
    if (!taggedUsers.some((u) => u.userId === user.userId)) {
      onTaggedUsersChange([...taggedUsers, user]);
    }
    closeList();

    const nextCaret = mention.start + insert.length;
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlighted((h) => (h + 1) % results.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlighted((h) => (h - 1 + results.length) % results.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectUser(results[highlighted]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeList();
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit?.();
    }
  };

  return (
    <div style={{ position: 'relative', flex: 1, display: 'flex' }}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={(e) => updateMention(value, e.currentTarget.selectionStart ?? value.length)}
        onBlur={() => setTimeout(closeList, 150)}
        placeholder={placeholder}
        disabled={disabled}
        role="combobox"
        aria-label="Message"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-activedescendant={isOpen ? `${listId}-${highlighted}` : undefined}
        autoComplete="off"
        style={style}
      />
      {isOpen && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Tag a user"
          style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            minWidth: '260px',
            maxHeight: '240px',
            overflowY: 'auto',
            margin: '0 0 4px 0',
            padding: '4px 0',
            listStyle: 'none',
            backgroundColor: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            zIndex: 1000,
          }}
        >
          {results.map((user, i) => (
            <li
              key={user.userId}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === highlighted}
              onMouseDown={(e) => {
                e.preventDefault(); // keep focus in the input
                selectUser(user);
              }}
              onMouseEnter={() => setHighlighted(i)}
              style={{
                padding: '6px 12px',
                cursor: 'pointer',
                backgroundColor: i === highlighted ? '#eff6ff' : undefined,
              }}
            >
              <div style={{ fontSize: '13px', fontWeight: 500, color: '#111827' }}>{user.displayName}</div>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>{user.email}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
